import React, { ChangeEvent, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useQueryClient } from 'react-query';
import { Paper } from '@material-ui/core';
import { pull, uniq, intersectionWith } from 'lodash';

import {
  ClientInvitationStatus,
  useGetClientInvitationsByClientQuery,
  useDeleteClientInvitationsMutation,
} from 'api';
import { Alert, EmptyState } from 'components';
import { adminPeopleInvite } from 'paths';
import { useGetClientInvitationsByClient } from 'features/admin/hooks';
import { useDeleteClientInvitations } from 'features/admin/updaters';

import { Header, AcceptedTable, PendingTable } from '../components/Invitations';

const LIMIT = 24;

const Invitations: FC = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Data State
  const [filter, setFilter] = useState(ClientInvitationStatus.Active);
  const [checkedIds, setCheckedIds] = useState([]);

  // UI State
  const [allChecked, setAllChecked] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const isPending = filter === ClientInvitationStatus.Active;
  const canResend = isPending && !!checkedIds.length;
  const variables = { invitation_status: filter, limit: LIMIT };

  const { isEmpty, isExhausted, isInitialLoad, results, count, sentinelRef } =
    useGetClientInvitationsByClient(variables);
  const mutationOptions = useDeleteClientInvitations(variables);
  const { mutate: deleteClientInvitationsMutation } = useDeleteClientInvitationsMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries(useGetClientInvitationsByClientQuery.getKey(variables));
    },
    ...mutationOptions,
  });

  function handleChecked(event: ChangeEvent<HTMLInputElement>) {
    const { checked, name } = event.target;

    checked ? checkedIds.unshift(name) : pull(checkedIds, name);
    setCheckedIds(uniq(checkedIds));
  }

  function handleAllChecked(event: ChangeEvent<HTMLInputElement>) {
    const { checked } = event.target;
    const ids = checked ? results.map(result => result.id) : [];

    setCheckedIds(ids);
    setAllChecked(checked);
  }

  function handleFilterChange(value: ClientInvitationStatus) {
    setFilter(value);
  }

  function handleResend() {
    const selectedInvitations = intersectionWith(
      results,
      checkedIds,
      (first, second) => first.id === second
    );

    const uniqueEmails = uniq(selectedInvitations.map(invitation => invitation.email_address));

    history.push(adminPeopleInvite, { emails: uniqueEmails });
  }

  function handleConfirmDelete() {
    deleteClientInvitationsMutation({
      invitation_ids: checkedIds,
    });

    setDeleteOpen(false);
    setAllChecked(false);
    setCheckedIds([]);
  }

  function renderTable() {
    if (isEmpty) {
      return <EmptyState name="invitations" />;
    }

    if (isPending) {
      return (
        <PendingTable
          results={results}
          sentinelRef={sentinelRef}
          isInitialLoad={isInitialLoad}
          isAllChecked={allChecked}
          isExhausted={isExhausted}
          checkedIds={checkedIds}
          onAllChecked={handleAllChecked}
          onChecked={handleChecked}
        />
      );
    }

    return (
      <AcceptedTable
        results={results}
        sentinelRef={sentinelRef}
        isInitialLoad={isInitialLoad}
        isExhausted={isExhausted}
      />
    );
  }

  return (
    <Paper>
      <Header
        filter={filter}
        total={count}
        checkedTotal={checkedIds.length}
        canResend={canResend}
        onChange={handleFilterChange}
        onResend={handleResend}
        onDelete={() => setDeleteOpen(true)}
      />
      {renderTable()}
      <Alert
        open={deleteOpen}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteOpen(false)}
        message={t('admin:deleteInvitations', { count: checkedIds.length })}
        confirm={t('common:delete')}
      />
    </Paper>
  );
};

export default Invitations;
